import React, { useCallback, useEffect, useRef, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useIdleTimer } from "react-idle-timer";
import { Bell } from "react-feather";
import { CSSTransition } from "react-transition-group";
import diffText from "date-fns/formatDistanceStrict";
import parse from "date-fns/parseISO";
import classnames from "classnames";

import { accountApi, Notification } from "../../api";
import { longDateTime } from "../../common/helpers/date";
import plugins from "./notifications/Plugins";
import useIntervalAsync from "../../common/hooks/useIntervalAsync";

const pollIntervalMs = parseInt(process.env.REACT_APP_NOTIFICATIONS_POLL_INTERVAL_SECONDS ?? "5") * 1000;
const pollIdleTimeoutMs = parseInt(process.env.REACT_APP_NOTIFICATIONS_POLL_IDLE_TIMEOUT_SECONDS ?? "0") * 1000;
const pollIdleDebounceMs = parseInt(process.env.REACT_APP_NOTIFICATIONS_POLL_IDLE_DEBOUNCE_MS ?? "1000");

interface ItemProps {
  item: Notification;
  now: Date;
}

const Item: React.FC<ItemProps> = ({ item, now }) => {
  const Icon = plugins.getIcon(item);
  const title = plugins.getTitle(item);
  const description = plugins.getDescription(item);
  const d = parse(item.created);
  const diff = `${diffText(d, now)} ago`;
  const date = longDateTime(d);
  return (
    <div className="d-flex pr-4 position-relative">
      <div className="p-2 text-muted">
        <Icon className="p-1 bg-neutral text-white" style={{ borderRadius: "50%" }} />
      </div>
      <div className="py-2 flex-grow-1">
        <div className="text-muted text-uppercase fs-10">{title}</div>
        <div>
          {!!item.url
            ? <a href={item.url} className="stretched-link">{description}</a>
            : <span>{description}</span>}
        </div>
        <div className="text-right text-muted fs-10">
          <span title={date}>{diff}</span>
        </div>
      </div>
    </div>);
};

const Notifications: React.FC = () => {

  const oldestId = useRef<number>();
  const newestId = useRef<number>();

  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<Notification[]>([]);
  const [anyOlder, setAnyOlder] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const anyUnread = unreadCount !== 0;
  const anyItems = items.length !== 0;
  const count = unreadCount < 100 ? unreadCount.toString() : "99+";

  const init = async () => {
    await fetchData();
    startPolling();
  };

  const toggle = async () => {
    if (!isOpen) {
      stopPolling();
      setNow(new Date());
    }
    else {
      if (newestId.current && unreadCount !== 0) {
        await markAsRead(newestId.current);
      }
      startPolling();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (lastId: number) => {
    setUnreadCount(0);
    setItems(items => items.map(x => lastId < x.id || x.isRead ? x : { ...x, isRead: true }));
    await accountApi.markNotificationsAsRead({ lastId });
  };

  const appendItems = (itemsToAppend: Notification[]) => {
    setItems(items.concat(itemsToAppend));
  };

  const prependItems = (itemsToPrepend: Notification[]) => {
    setItems(items => itemsToPrepend.filter(x => !items.some(xx => xx.id === x.id)).concat(items));
  };

  const fetchData = async () => {
    const response = await accountApi.getNotifications();
    setUnreadCount(response.data.unreadCount);
    if (response.data.items.length) {
      setItems(response.data.items);
      oldestId.current = response.data.items[response.data.items.length - 1].id;
      newestId.current = response.data.items[0].id;
    }
    else if (items.length) {
      setItems([]);
    }
    setAnyOlder(!!response.data.anyMoreItems);
  };

  const fetchDataAndAppend = async (initialId: number) => {
    const response = await accountApi.getNotifications({
      initialId,
    });
    setUnreadCount(response.data.unreadCount);
    if (response.data.items.length) {
      appendItems(response.data.items);
      oldestId.current = response.data.items[response.data.items.length - 1].id;
    }
    setAnyOlder(!!response.data.anyMoreItems);
  };

  const fetchDataAndPrepend = async (initialId?: number) => {
    const response = await accountApi.getNotifications({
      ...(typeof initialId === "number" ? { initialId } : {}),
      direction: "up",
    });
    setUnreadCount(response.data.unreadCount);
    if (response.data.items.length) {
      prependItems(response.data.items);
      newestId.current = response.data.items[0].id;
    }
  };

  const getOlderItems = async () => {
    if (typeof oldestId === "number") {
      setIsLoadingOlder(true);
      try {
        await fetchDataAndAppend(oldestId - 1);
      }
      catch {

      }
      setIsLoadingOlder(false);
    }
  };

  const poll = useCallback(async () => {
    await getNewerItems();
  }, []);

  const getNewerItems = async () => {
    await fetchDataAndPrepend(typeof newestId.current === "number" ? newestId.current + 1 : void 0);
  };

  const startPolling = () => {
    setIsPolling(true);
  }

  const stopPolling = () => {
    setIsPolling(false);
  }

  if (pollIntervalMs) {
    useIntervalAsync(poll, pollIntervalMs, isPolling);
  }

  if (pollIdleTimeoutMs) {
    useIdleTimer({
      timeout: pollIdleTimeoutMs,
      onIdle: stopPolling,
      onActive: startPolling,
      debounce: pollIdleDebounceMs,
    });
  }

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoadingOlder,
    hasNextPage: anyOlder,
    onLoadMore: () => getOlderItems(),
    rootMargin: '0px 100px 0px 0px',
    delayInMs: 1000,
  });

  useEffect(() => {
    init();
  }, []);

  return (
    <div className="d-flex">
      <div className={classnames({
        "d-flex position-relative": true,
        "bg-panel-light": anyUnread,
      })}>
        <a href="#0"
          className={classnames({
            "stretched-link px-2 py-1 d-flex align-items-center": true,
            "text-primary": anyUnread,
          })}
          onClick={(e) => {
            e.preventDefault();
            toggle();
          }}>
          <Bell />
        </a>
        {anyUnread && (
          <div className="position-absolute mx-1"
            style={{
              right: 0,
            }}>
            <div className="badge badge-pill badge-primary">{count}</div>
          </div>
        )}
      </div>
      <CSSTransition
        classNames="fade-top"
        in={isOpen}
        timeout={{
          appear: 200,
          enter: 200,
          exit: 100,
        }}
        unmountOnExit
      >
        <div className="position-absolute bg-light border-left border-bottom box-shadow scroll-auto custom-scrollbar"
          style={{
            top: "100%",
            right: 0,
            width: "20rem",
            marginTop: "1px",
            maxHeight: "400px",
            zIndex: 1,
          }}
          ref={rootRef}>
          {anyItems
            ? (
              <>
                <ul className="list-none">
                  {items.map((x, i) => (
                    <li key={x.id}
                      className={classnames({
                        "border-top": i !== 0,
                        "bg-panel-light": x.isRead,
                        "bg-light": !x.isRead,
                      })}>
                      <Item item={x} now={now} />
                    </li>
                  ))}
                </ul>
                {isLoadingOlder && (
                  <div ref={sentryRef} className="px-2 py-1 bg-neutral text-center text-white">
                    Loading...
                  </div>
                )}
              </>
            )
            : (
              <div className="mx-3 my-2 text-center text-muted">
                No notifications
              </div>
            )}
        </div>
      </CSSTransition>
    </div>
  );
}

export default Notifications;
